import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Plus, Edit2, Trash2, Users, FileText, Loader2, CheckCircle, X, Search, Upload } from 'lucide-react';
import { toast } from 'sonner';
import API from '../api';

const LeavePolicyManagement = ({ user, setUser }) => {
  const [policies, setPolicies] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('policies');
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [showIndividualForm, setShowIndividualForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [allocSearchTerm, setAllocSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const fileInputRef = useRef(null);

  const [policyForm, setPolicyForm] = useState({
    name: '',
    description: '',
    casual_leave: 12,
    sick_leave: 0,
    earned_leave: 0,
    maternity_leave: 0,
    paternity_leave: 0,
    unpaid_leave: 0,
    probation_months: 3,
    accrual_type: 'monthly',
    carry_forward_allowed: false,
    max_carry_forward: 0,
    encashment_allowed: false
  });

  const [applyForm, setApplyForm] = useState({
    policy_id: '',
    department: '',
    designation: '',
    year: new Date().getFullYear()
  });

  const [individualForm, setIndividualForm] = useState({
    employee_id: '',
    casual_leave: 0,
    sick_leave: 0,
    earned_leave: 0,
    maternity_leave: 0,
    paternity_leave: 0,
    unpaid_leave: 0,
    year: new Date().getFullYear()
  });

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [policiesRes, allocationsRes, employeesRes, deptRes, desigRes] = await Promise.all([
        API.get('/leave-policies'),
        API.get(`/employee-leave-allocations?year=${selectedYear}`),
        API.get('/employees'),
        API.get('/departments'),
        API.get('/designations')
      ]);
      setPolicies(policiesRes.data);
      setAllocations(allocationsRes.data);
      setEmployees(employeesRes.data.filter(e => e.employment_status !== 'Exit'));
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploadingPdf(true);
    setExtractedText('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await API.post('/leave-policies/parse-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const extracted = response.data.extracted_policy;
        setPolicyForm({
          name: extracted.name || '',
          description: extracted.description || '',
          casual_leave: extracted.casual_leave || 0,
          sick_leave: extracted.sick_leave || 0,
          earned_leave: extracted.earned_leave || 0,
          maternity_leave: extracted.maternity_leave || 0,
          paternity_leave: extracted.paternity_leave || 0,
          unpaid_leave: extracted.unpaid_leave || 0,
          probation_months: extracted.probation_months || 3,
          carry_forward_allowed: extracted.carry_forward_allowed || false,
          max_carry_forward: extracted.max_carry_forward || 0,
          encashment_allowed: extracted.encashment_allowed || false
        });
        setExtractedText(extracted.raw_text || '');
        setShowPolicyForm(true);
        toast.success('PDF parsed successfully! Please review the extracted values.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse PDF');
    } finally {
      setUploadingPdf(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreatePolicy = async () => {
    try {
      if (editingPolicy) {
        await API.put(`/leave-policies/${editingPolicy.id}`, policyForm);
        toast.success('Policy updated successfully');
      } else {
        await API.post('/leave-policies', policyForm);
        toast.success('Policy created successfully');
      }
      setShowPolicyForm(false);
      setEditingPolicy(null);
      setPolicyForm({
        name: '',
        description: '',
        casual_leave: 12,
        sick_leave: 0,
        earned_leave: 0,
        maternity_leave: 0,
        paternity_leave: 0,
        unpaid_leave: 0,
        probation_months: 3,
        accrual_type: 'monthly',
        carry_forward_allowed: false,
        max_carry_forward: 0,
        encashment_allowed: false
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to save policy');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    try {
      await API.delete(`/leave-policies/${policyId}`);
      toast.success('Policy deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleApplyPolicy = async () => {
    try {
      const response = await API.post('/apply-leave-policy', applyForm);
      toast.success(response.data.message);
      setShowApplyForm(false);
      setApplyForm({
        policy_id: '',
        department: '',
        designation: '',
        year: new Date().getFullYear()
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to apply policy');
    }
  };

  const handleIndividualAllocation = async () => {
    try {
      await API.post('/employee-leave-allocations', individualForm);
      toast.success('Leave allocation saved successfully');
      setShowIndividualForm(false);
      setSelectedEmployee(null);
      setIndividualForm({
        employee_id: '',
        casual_leave: 0,
        sick_leave: 0,
        earned_leave: 0,
        maternity_leave: 0,
        paternity_leave: 0,
        unpaid_leave: 0,
        year: new Date().getFullYear()
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to save allocation');
    }
  };

  const editPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      name: policy.name,
      description: policy.description || '',
      casual_leave: policy.casual_leave || 0,
      sick_leave: policy.sick_leave || 0,
      earned_leave: policy.earned_leave || 0,
      maternity_leave: policy.maternity_leave || 0,
      paternity_leave: policy.paternity_leave || 0,
      unpaid_leave: policy.unpaid_leave || 0,
      probation_months: policy.probation_months || 3,
      accrual_type: policy.accrual_type || 'monthly',
      carry_forward_allowed: policy.carry_forward_allowed || false,
      max_carry_forward: policy.max_carry_forward || 0,
      encashment_allowed: policy.encashment_allowed || false
    });
    setShowPolicyForm(true);
  };

  const openIndividualForm = (employee) => {
    setSelectedEmployee(employee);
    const existing = allocations.find(a => a.employee_id === employee.id);
    setIndividualForm({
      employee_id: employee.id,
      casual_leave: existing?.casual_leave || 0,
      sick_leave: existing?.sick_leave || 0,
      earned_leave: existing?.earned_leave || 0,
      maternity_leave: existing?.maternity_leave || 0,
      paternity_leave: existing?.paternity_leave || 0,
      unpaid_leave: existing?.unpaid_leave || 0,
      year: selectedYear
    });
    setShowIndividualForm(true);
  };

  const filteredEmployees = employees.filter(emp => {
    const name = (emp.first_name && emp.last_name 
      ? `${emp.first_name} ${emp.last_name}` 
      : emp.name || emp.employee_id || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    const designation = (emp.designation || emp.job_title || '').toLowerCase();
    return name.includes(search) || emp.employee_id?.toLowerCase().includes(search) || designation.includes(search);
  });

  const hasAccess = useSectionAccess('/leave-policy');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leave Policy Management</h1>
            <p className="text-gray-500 mt-1">Manage leave policies and allocations for employees</p>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border rounded-md px-3 py-2"
            >
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('policies')}
            className={`px-4 py-2 font-medium ${activeTab === 'policies' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            Leave Policies
          </button>
          <button
            onClick={() => setActiveTab('allocations')}
            className={`px-4 py-2 font-medium ${activeTab === 'allocations' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            Employee Allocations
          </button>
          <button
            onClick={() => setActiveTab('individual')}
            className={`px-4 py-2 font-medium ${activeTab === 'individual' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            Individual Assignment
          </button>
        </div>

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => { setEditingPolicy(null); setExtractedText(''); setShowPolicyForm(true); }} data-testid="create-policy-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Policy
              </Button>
              <div className="relative">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  className="hidden"
                  id="pdf-upload"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPdf}
                  data-testid="upload-pdf-btn"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  {uploadingPdf ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {uploadingPdf ? 'Parsing PDF...' : 'Upload PDF'}
                </Button>
              </div>
              <Button variant="outline" onClick={() => setShowApplyForm(true)} data-testid="apply-policy-btn">
                <Users className="h-4 w-4 mr-2" /> Apply Policy to Employees
              </Button>
            </div>

            {/* Policy Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {policies.map(policy => (
                <Card key={policy.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{policy.name}</CardTitle>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => editPolicy(policy)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePolicy(policy.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <CardDescription>{policy.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Casual Leave:</span>
                        <span className="font-medium">{policy.casual_leave}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Sick Leave:</span>
                        <span className="font-medium">{policy.sick_leave}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Earned Leave:</span>
                        <span className="font-medium">{policy.earned_leave}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Probation:</span>
                        <span className="font-medium">{policy.probation_months} months</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Accrual:</span>
                        <span className={`font-medium ${(policy.accrual_type || 'monthly') === 'monthly' ? 'text-orange-600' : 'text-gray-700'}`}>
                          {(policy.accrual_type || 'monthly') === 'monthly'
                            ? `Monthly (${((policy.casual_leave + policy.sick_leave + policy.earned_leave) / 12).toFixed(1)}/mo)`
                            : 'Annual (all at once)'}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      {policy.carry_forward_allowed && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Carry Forward</span>
                      )}
                      {policy.encashment_allowed && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Encashment</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {policies.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-500">
                  No leave policies created yet. Click "Create Policy" to add one.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Allocations Tab */}
        {activeTab === 'allocations' && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle>Employee Leave Allocations - {selectedYear}</CardTitle>
                    <CardDescription>View all employees and their assigned leave quotas with remaining balance</CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, dept, designation..."
                      value={allocSearchTerm}
                      onChange={(e) => setAllocSearchTerm(e.target.value)}
                      className="pl-8 w-64 h-9"
                      data-testid="alloc-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Employee</th>
                        <th className="text-left p-3">Department</th>
                        <th className="text-left p-3">Designation</th>
                        <th className="text-center p-3">CL (Alloc/Used/Rem)</th>
                        <th className="text-center p-3">SL (Alloc/Used/Rem)</th>
                        <th className="text-center p-3">EL (Alloc/Used/Rem)</th>
                        <th className="text-left p-3">Policy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocations
                        .filter(alloc => {
                          if (!allocSearchTerm) return true;
                          const q = allocSearchTerm.toLowerCase();
                          return (alloc.employee_name || '').toLowerCase().includes(q) ||
                            (alloc.department || '').toLowerCase().includes(q) ||
                            (alloc.designation || '').toLowerCase().includes(q) ||
                            (alloc.employee_id || '').toLowerCase().includes(q);
                        })
                        .map(alloc => (
                        <tr key={alloc.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{alloc.employee_name || 'N/A'}</td>
                          <td className="p-3">{alloc.department || '-'}</td>
                          <td className="p-3">{alloc.designation || '-'}</td>
                          <td className="p-3 text-center">
                            <span className="text-blue-600">{alloc.casual_leave || 0}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className="text-orange-600">{alloc.used_casual || 0}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className={`font-bold ${(alloc.remaining_casual || alloc.casual_leave || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {alloc.remaining_casual !== undefined ? alloc.remaining_casual : alloc.casual_leave || 0}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-blue-600">{alloc.sick_leave || 0}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className="text-orange-600">{alloc.used_sick || 0}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className={`font-bold ${(alloc.remaining_sick || alloc.sick_leave || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {alloc.remaining_sick !== undefined ? alloc.remaining_sick : alloc.sick_leave || 0}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="text-blue-600">{alloc.earned_leave || 0}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className="text-orange-600">{alloc.used_earned || 0}</span>
                            <span className="text-gray-400 mx-1">/</span>
                            <span className={`font-bold ${(alloc.remaining_earned || alloc.earned_leave || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {alloc.remaining_earned !== undefined ? alloc.remaining_earned : alloc.earned_leave || 0}
                            </span>
                          </td>
                          <td className="p-3">
                            {alloc.policy_name ? (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{alloc.policy_name}</span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Manual</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {allocations.filter(a => {
                        if (!allocSearchTerm) return true;
                        const q = allocSearchTerm.toLowerCase();
                        return (a.employee_name || '').toLowerCase().includes(q) || (a.department || '').toLowerCase().includes(q) || (a.designation || '').toLowerCase().includes(q) || (a.employee_id || '').toLowerCase().includes(q);
                      }).length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-gray-500">
                            {allocSearchTerm ? 'No employees matching your search' : `No leave allocations for ${selectedYear}. Apply a policy or assign individually.`}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Legend */}
                <div className="mt-4 flex gap-4 text-xs text-gray-500">
                  <span><span className="text-blue-600 font-medium">Blue</span> = Allocated</span>
                  <span><span className="text-orange-600 font-medium">Orange</span> = Used</span>
                  <span><span className="text-green-600 font-medium">Green</span> = Remaining</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Individual Assignment Tab */}
        {activeTab === 'individual' && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search employees by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEmployees.map(emp => {
                const allocation = allocations.find(a => a.employee_id === emp.id);
                const empName = emp.first_name && emp.last_name 
                  ? `${emp.first_name} ${emp.last_name}` 
                  : emp.name || emp.employee_id || 'Unknown';
                const empDesignation = emp.designation || emp.job_title || emp.position || 'N/A';
                const empDepartment = emp.department || 'N/A';
                
                return (
                  <Card key={emp.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">{empName}</CardTitle>
                          <CardDescription className="text-sm font-medium text-slate-600">{emp.employee_id}</CardDescription>
                          <p className="text-xs text-slate-500 mt-1">{empDesignation}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => openIndividualForm(emp)}>
                          <Edit2 className="h-3 w-3 mr-1" /> Assign
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {empDepartment !== 'N/A' && (
                        <div className="text-xs text-gray-500 mb-2">
                          {empDepartment}
                        </div>
                      )}
                      {allocation ? (
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-center p-2 bg-green-50 rounded">
                            <div className="font-bold text-green-700">{allocation.casual_leave}</div>
                            <div className="text-gray-500">CL</div>
                          </div>
                          <div className="text-center p-2 bg-blue-50 rounded">
                            <div className="font-bold text-blue-700">{allocation.sick_leave}</div>
                            <div className="text-gray-500">SL</div>
                          </div>
                          <div className="text-center p-2 bg-purple-50 rounded">
                            <div className="font-bold text-purple-700">{allocation.earned_leave}</div>
                            <div className="text-gray-500">EL</div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded text-center">
                          No leave allocated
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Create/Edit Policy Modal */}
        {showPolicyForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">{editingPolicy ? 'Edit Policy' : 'Create Leave Policy'}</h2>
                <Button variant="ghost" size="sm" onClick={() => { setShowPolicyForm(false); setExtractedText(''); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {extractedText && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-800">PDF Parsed Successfully</span>
                  </div>
                  <p className="text-sm text-green-700 mb-2">Values have been auto-filled from the PDF. Please review and adjust as needed.</p>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-green-600 hover:text-green-800">View extracted text</summary>
                    <pre className="mt-2 p-2 bg-white rounded text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">{extractedText}</pre>
                  </details>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label>Policy Name *</Label>
                  <Input
                    value={policyForm.name}
                    onChange={(e) => setPolicyForm({...policyForm, name: e.target.value})}
                    placeholder="e.g., DC/TL Leave Policy"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Input
                    value={policyForm.description}
                    onChange={(e) => setPolicyForm({...policyForm, description: e.target.value})}
                    placeholder="Policy description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Casual Leave</Label>
                    <Input
                      type="number"
                      value={policyForm.casual_leave}
                      onChange={(e) => setPolicyForm({...policyForm, casual_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Sick Leave</Label>
                    <Input
                      type="number"
                      value={policyForm.sick_leave}
                      onChange={(e) => setPolicyForm({...policyForm, sick_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Earned Leave</Label>
                    <Input
                      type="number"
                      value={policyForm.earned_leave}
                      onChange={(e) => setPolicyForm({...policyForm, earned_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Probation (Months)</Label>
                    <Input
                      type="number"
                      value={policyForm.probation_months}
                      onChange={(e) => setPolicyForm({...policyForm, probation_months: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Accrual Type</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={policyForm.accrual_type}
                      onChange={(e) => setPolicyForm({...policyForm, accrual_type: e.target.value})}
                      data-testid="accrual-type-select"
                    >
                      <option value="monthly">Monthly (1.5/month after probation)</option>
                      <option value="annual">Annual (all at once)</option>
                    </select>
                    {policyForm.accrual_type === 'monthly' && (
                      <p className="text-xs text-orange-600 mt-1">
                        Monthly rate: {((policyForm.casual_leave + policyForm.sick_leave + policyForm.earned_leave) / 12).toFixed(2)} leaves/month
                        (starts after {policyForm.probation_months} months)
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Maternity Leave</Label>
                    <Input
                      type="number"
                      value={policyForm.maternity_leave}
                      onChange={(e) => setPolicyForm({...policyForm, maternity_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Paternity Leave</Label>
                    <Input
                      type="number"
                      value={policyForm.paternity_leave}
                      onChange={(e) => setPolicyForm({...policyForm, paternity_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={policyForm.carry_forward_allowed}
                      onChange={(e) => setPolicyForm({...policyForm, carry_forward_allowed: e.target.checked})}
                    />
                    <span className="text-sm">Carry Forward Allowed</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={policyForm.encashment_allowed}
                      onChange={(e) => setPolicyForm({...policyForm, encashment_allowed: e.target.checked})}
                    />
                    <span className="text-sm">Encashment Allowed</span>
                  </label>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowPolicyForm(false)}>Cancel</Button>
                  <Button onClick={handleCreatePolicy}>{editingPolicy ? 'Update' : 'Create'} Policy</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Apply Policy Modal */}
        {showApplyForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Apply Policy to Employees</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowApplyForm(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Select Policy *</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={applyForm.policy_id}
                    onChange={(e) => setApplyForm({...applyForm, policy_id: e.target.value})}
                  >
                    <option value="">-- Select Policy --</option>
                    {policies.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Department (Optional)</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={applyForm.department}
                    onChange={(e) => setApplyForm({...applyForm, department: e.target.value, designation: ''})}
                  >
                    <option value="">-- All Departments --</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Designation (Optional)</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={applyForm.designation}
                    onChange={(e) => setApplyForm({...applyForm, designation: e.target.value})}
                  >
                    <option value="">-- All Designations --</option>
                    {designations
                      .filter(d => {
                        if (!applyForm.department) return true;
                        const selectedDept = departments.find(dept => dept.name === applyForm.department);
                        return selectedDept && d.department_id === selectedDept.id;
                      })
                      .map(d => (
                        <option key={d.id} value={d.title || d.name}>{d.title || d.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <Label>Year</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={applyForm.year}
                    onChange={(e) => setApplyForm({...applyForm, year: parseInt(e.target.value)})}
                  >
                    {[2024, 2025, 2026, 2027].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowApplyForm(false)}>Cancel</Button>
                  <Button onClick={handleApplyPolicy} disabled={!applyForm.policy_id}>Apply Policy</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Individual Allocation Modal */}
        {showIndividualForm && selectedEmployee && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Assign Leave - {selectedEmployee.first_name} {selectedEmployee.last_name}</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowIndividualForm(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Casual Leave</Label>
                    <Input
                      type="number"
                      value={individualForm.casual_leave}
                      onChange={(e) => setIndividualForm({...individualForm, casual_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Sick Leave</Label>
                    <Input
                      type="number"
                      value={individualForm.sick_leave}
                      onChange={(e) => setIndividualForm({...individualForm, sick_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Earned Leave</Label>
                    <Input
                      type="number"
                      value={individualForm.earned_leave}
                      onChange={(e) => setIndividualForm({...individualForm, earned_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Year</Label>
                    <select
                      className="w-full border rounded-md p-2"
                      value={individualForm.year}
                      onChange={(e) => setIndividualForm({...individualForm, year: parseInt(e.target.value)})}
                    >
                      {[2024, 2025, 2026, 2027].map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Maternity Leave</Label>
                    <Input
                      type="number"
                      value={individualForm.maternity_leave}
                      onChange={(e) => setIndividualForm({...individualForm, maternity_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Paternity Leave</Label>
                    <Input
                      type="number"
                      value={individualForm.paternity_leave}
                      onChange={(e) => setIndividualForm({...individualForm, paternity_leave: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowIndividualForm(false)}>Cancel</Button>
                  <Button onClick={handleIndividualAllocation}>Save Allocation</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LeavePolicyManagement;
